import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

const SECTOR_COUNT = 15
const SECTOR_DEG = 360 / SECTOR_COUNT
const WHEEL_START_ANGLE = -90
const DEFAULT_SPIN_DURATION_MS = 10000

const getSectorColor = (sector) => {
  if (sector === 0) return 'green'
  return sector % 2 === 0 ? 'red' : 'black'
}

const getSectorRotation = (sector) => {
  const normalized = Number.isFinite(Number(sector)) ? Number(sector) : 0
  const centerAngle = WHEEL_START_ANGLE + normalized * SECTOR_DEG + SECTOR_DEG / 2
  return -centerAngle
}

const buildGradient = () => {
  const colorMap = {
    red: '#ef4444',
    black: '#111827',
    green: '#22c55e'
  }

  const stops = []
  for (let i = 0; i < SECTOR_COUNT; i += 1) {
    const color = colorMap[getSectorColor(i)]
    const start = i * SECTOR_DEG
    const end = start + SECTOR_DEG
    stops.push(`${color} ${start}deg ${end}deg`)
  }

  return `conic-gradient(from ${WHEEL_START_ANGLE}deg, ${stops.join(', ')})`
}

const formatTimer = (millisecondsValue) => {
  const safeMilliseconds = Math.max(0, Number(millisecondsValue || 0))
  const totalTenths = Math.floor(safeMilliseconds / 100)
  const seconds = Math.floor(totalTenths / 10)
  const tenths = totalTenths % 10
  return `${seconds}.${tenths}`
}

const DoubleWheel = ({ roundId, status, prevSector, winSector, winColor, countdownMs, raffleSeconds }) => {
  const { t } = useTranslation()
  const [rotation, setRotation] = useState(() => getSectorRotation(prevSector))
  const [isAnimating, setIsAnimating] = useState(false)
  const [showNewStatusLabel, setShowNewStatusLabel] = useState(
    status === 'new' && Number(countdownMs || 0) >= 7000
  )
  const animatedRoundRef = useRef(null)

  const sectorGradient = useMemo(() => buildGradient(), [])
  const displaySector = Number.isFinite(Number(winSector))
    ? Number(winSector)
    : Number.isFinite(Number(prevSector))
      ? Number(prevSector)
      : 0

  const statusLabel = useMemo(() => {
    const statusMap = {
      new: t('double.status.new', 'Делайте ставки'),
      started: t('double.status.started', 'Ставки открыты'),
      raffle: t('double.status.raffle', 'Розыгрыш'),
      result: t('double.status.result', 'Результат'),
      finished: t('double.status.finished', 'Делайте ставки')
    }

    return statusMap[status] || status || t('double.status.unknown', 'Неизвестно')
  }, [status, t])

  const isResultStatus = status === 'result'
  const isStartedStatus = status === 'started'
  const isNewStatus = status === 'new'
  const isTimerVisible = isStartedStatus || (isNewStatus && !showNewStatusLabel)
  const isRaffleStatus = status === 'raffle'
  const isFinishedStatus = ['result', 'finished'].includes(status)
  const showGlow = ['new', 'started', 'raffle', 'result', 'finished'].includes(status)
  const showRedGlow = isRaffleStatus || isFinishedStatus
  const redGlowSpinClass = 'bg-[conic-gradient(from_0deg,rgba(239,68,68,0.12),rgba(248,113,113,0.52),rgba(239,68,68,0.12),rgba(220,38,38,0.58),rgba(239,68,68,0.12))]'
  const redGlowPulseClass = 'bg-[radial-gradient(circle,rgba(248,113,113,0.5)_0%,rgba(239,68,68,0.26)_38%,rgba(239,68,68,0)_72%)]'
  const greenGlowSpinClass = 'bg-[conic-gradient(from_0deg,rgba(16,185,129,0.12),rgba(34,211,238,0.48),rgba(16,185,129,0.12),rgba(34,211,238,0.54),rgba(16,185,129,0.12))]'
  const greenGlowPulseClass = 'bg-[radial-gradient(circle,rgba(34,211,238,0.5)_0%,rgba(16,185,129,0.24)_38%,rgba(16,185,129,0)_72%)]'
  const spinDurationMs = useMemo(() => {
    if (status !== 'raffle') {
      return DEFAULT_SPIN_DURATION_MS
    }

    const parsedRaffleSeconds = Number(raffleSeconds)
    if (!Number.isFinite(parsedRaffleSeconds) || parsedRaffleSeconds <= 0) {
      return DEFAULT_SPIN_DURATION_MS
    }

    return Math.round(parsedRaffleSeconds * 1000)
  }, [raffleSeconds, status])
  const fullTurns = useMemo(() => {
    if (status !== 'raffle') {
      return 6
    }

    const parsedRaffleSeconds = Number(raffleSeconds)
    if (!Number.isFinite(parsedRaffleSeconds) || parsedRaffleSeconds <= 0) {
      return 6
    }

    if (parsedRaffleSeconds <= 2) return 1
    if (parsedRaffleSeconds <= 4) return 2
    if (parsedRaffleSeconds <= 6) return 3
    if (parsedRaffleSeconds <= 8) return 6
    if (parsedRaffleSeconds <= 10) return 8
    return 6
  }, [raffleSeconds, status])

  const centerStyle = useMemo(() => {
    if (!isResultStatus) {
      return { backgroundColor: 'rgba(10, 10, 15, 0.95)', color: '#ffffff' }
    }

    const normalizedColor = String(winColor || getSectorColor(displaySector)).toLowerCase()

    if (normalizedColor === 'red') {
      return { backgroundColor: '#ef4444', color: '#ffffff' }
    }

    if (normalizedColor === 'black') {
      return { backgroundColor: '#111827', color: '#ffffff' }
    }

    return { backgroundColor: '#22c55e', color: '#052e16' }
  }, [displaySector, isResultStatus, winColor])

  useEffect(() => {
    if (!roundId) return
    animatedRoundRef.current = null
    setIsAnimating(false)
    setRotation(getSectorRotation(prevSector))
  }, [roundId, prevSector])

  useEffect(() => {
    if (status !== 'new') {
      setShowNewStatusLabel(false)
      return undefined
    }

    const millisecondsBeforeRaffle = Number(countdownMs || 0)
    if (millisecondsBeforeRaffle < 7000) {
      setShowNewStatusLabel(false)
      return undefined
    }

    setShowNewStatusLabel(true)
    const timeoutId = setTimeout(() => {
      setShowNewStatusLabel(false)
    }, 2000)

    return () => clearTimeout(timeoutId)
  }, [roundId, status])

  useEffect(() => {
    if (!Number.isFinite(Number(winSector))) return

    if (status === 'raffle') {
      if (animatedRoundRef.current === roundId) return

      animatedRoundRef.current = roundId
      setIsAnimating(true)

      const targetRotation = getSectorRotation(winSector) - 360 * fullTurns
      requestAnimationFrame(() => {
        setRotation(targetRotation)
      })
      return
    }

    if (['result', 'finished'].includes(status)) {
      if (animatedRoundRef.current === roundId) return

      animatedRoundRef.current = roundId
      setIsAnimating(false)
      setRotation(getSectorRotation(winSector))
    }
  }, [fullTurns, roundId, status, winSector])

  const labels = Array.from({ length: SECTOR_COUNT }, (_, sector) => {
    const centerConicAngle = WHEEL_START_ANGLE + sector * SECTOR_DEG + SECTOR_DEG / 2
    const angle = centerConicAngle - 90
    const radius = 39
    const x = 50 + radius * Math.cos((angle * Math.PI) / 180)
    const y = 50 + radius * Math.sin((angle * Math.PI) / 180)
    const radialAngle = (Math.atan2(y - 50, x - 50) * 180) / Math.PI

    return {
      sector,
      x,
      y,
      rotation: radialAngle + 90,
      color: getSectorColor(sector)
    }
  })

  return (
    <div className="p-4 lg:p-6">

      <div className="relative mx-auto aspect-square w-full max-w-[340px]">
        <div
          className={`pointer-events-none absolute -inset-[10%] z-0 rounded-full transition-opacity duration-1500 ease-out ${showGlow ? 'opacity-100' : 'opacity-0'}`}
        >
          <div
            className={`absolute inset-0 rounded-full blur-xl animate-[spin_4.8s_linear_infinite] ${redGlowSpinClass} transition-opacity duration-700 ease-out ${showRedGlow ? 'opacity-100' : 'opacity-0'}`}
          />
          <div
            className={`absolute inset-3 rounded-full blur-2xl animate-[pulse_2.4s_ease-in-out_infinite] ${redGlowPulseClass} transition-opacity duration-700 ease-out ${showRedGlow ? 'opacity-100' : 'opacity-0'}`}
          />
          <div
            className={`absolute inset-0 rounded-full blur-xl animate-[spin_4.8s_linear_infinite] ${greenGlowSpinClass} transition-opacity duration-700 ease-out ${showRedGlow ? 'opacity-0' : 'opacity-100'}`}
          />
          <div
            className={`absolute inset-3 rounded-full blur-2xl animate-[pulse_2.4s_ease-in-out_infinite] ${greenGlowPulseClass} transition-opacity duration-700 ease-out ${showRedGlow ? 'opacity-0' : 'opacity-100'}`}
          />
        </div>

        <div className="absolute left-1/2 top-1 z-20 -translate-x-1/2">
          <div className="h-0 w-0 border-l-[12px] border-r-[12px] border-b-[20px] border-l-transparent border-r-transparent border-b-white drop-shadow-[0_0_10px_rgba(255,255,255,0.6)]" />
        </div>

        <div
          className="absolute inset-0 rounded-full shadow-[0_0_40px_rgba(0,0,0,0.35)]"
          style={{
            background: sectorGradient,
            transform: `rotate(${rotation}deg)`,
            transition: isAnimating ? `transform ${spinDurationMs}ms cubic-bezier(0.1, 0.25, 0.25, 1)` : 'none'
          }}
          onTransitionEnd={() => setIsAnimating(false)}
        >
          <svg viewBox="0 0 100 100" className="h-full w-full">
            {labels.map((label) => (
              <text
                key={label.sector}
                x={label.x}
                y={label.y}
                textAnchor="middle"
                dominantBaseline="middle"
                transform={`rotate(${label.rotation} ${label.x} ${label.y})`}
                fill={label.color === 'green' ? '#d1fae5' : '#ffffff'}
                fontSize="6"
                fontWeight="700"
              >
                {label.sector}
              </text>
            ))}
          </svg>
        </div>

        <div
          className="absolute inset-[25%] z-10 rounded-full flex items-center justify-center transition-colors duration-300"
          style={centerStyle}
        >
          <div className="relative h-9 w-full px-1.5">
            <span
              className={`absolute inset-0 flex items-center justify-center text-center leading-tight font-bold transition-opacity duration-500 ${isResultStatus ? 'text-xl' : 'text-sm'} ${isResultStatus || !isTimerVisible ? 'opacity-100' : 'opacity-0'}`}
            >
              {isResultStatus ? displaySector : statusLabel}
            </span>
            <span
              className={`absolute inset-0 flex items-center justify-center text-center leading-tight font-bold font-mono text-3xl lg:text-4xl transition-opacity duration-500 ${isTimerVisible ? 'opacity-100' : 'opacity-0'}`}
            >
              {formatTimer(countdownMs)}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DoubleWheel
